/* eslint-disable react/prop-types */
import React from "react";

const COLORS = ["#3B82F6", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];

function niceStep(range) {
  const raw = range / 5;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const m = [1, 2, 5, 10].find((x) => x * pow >= raw) || 10;
  return m * pow;
}

const numLabel = (label) => {
  const m = String(label).match(/\d+/);
  return m ? m[0] : String(label).charAt(0).toUpperCase();
};

const DESKTOP = { W: 800, H: 190, padL: 46, padR: 16, padT: 14, padB: 34, fAxis: 10, fScore: 11, fLabel: 10.5, dotR: 5, titleX: 12, twoLine: false, numeric: false };
const MOBILE = { W: 380, H: 300, padL: 36, padR: 14, padT: 24, padB: 52, fAxis: 11, fScore: 12.5, fLabel: 13, dotR: 5.5, titleX: 10, twoLine: false, numeric: true };

export default function ScoreProgressionOverview({ attempts = [], scoreProgression = [], maxScore = 0 }) {
  const list = Array.isArray(scoreProgression) ? scoreProgression : [];

  const points = attempts.map((a, i) => {
    const match =
      list.find((s) => s?.attemptCount === i) ??
      (list[i] && list[i].attemptCount === undefined ? list[i] : undefined);
    const score = match && typeof match.totalScore === "number" ? match.totalScore : null;
    return { label: a.title, score, color: COLORS[i % COLORS.length] };
  });

  const n = points.length || 1;
  const scored = points.filter((p) => p.score !== null);
  const scores = scored.map((p) => p.score);

  const hiRaw = Math.max(Number(maxScore) || 0, ...scores, 10);
  const loRaw = Math.min(0, ...scores);
  const step = niceStep(hiRaw - loRaw);

  const lo = loRaw < 0 ? (Math.floor(loRaw / step) - 1) * step : 0;
  const hi = hiRaw;
  const range = hi - lo || 1;

  const renderChart = (cfg) => {
    const { W, H, padL, padR, padT, padB, fAxis, fScore, fLabel, dotR, titleX, twoLine, numeric } = cfg;
    const innerW = W - padL - padR;
    const innerH = H - padT - padB;

    const toX = (i) => padL + ((i + 0.5) * innerW) / n;
    const toY = (v) => padT + ((hi - v) / range) * innerH;
    const y0 = toY(0);

    const ticks = [];
    for (let t = Math.ceil(lo / step) * step; t <= hi; t += step) ticks.push(t);
    if (hi - ticks[ticks.length - 1] > step * 0.3) ticks.push(hi);

    const linePts = points
      .map((p, i) => (p.score !== null ? { x: toX(i), y: toY(p.score) } : null))
      .filter(Boolean);
    const lineD = linePts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    const areaD =
      linePts.length > 1
        ? `${lineD} L ${linePts[linePts.length - 1].x} ${y0} L ${linePts[0].x} ${y0} Z`
        : "";

    const barW = Math.min(40, (innerW / n) * 0.5);

   
    const plotBottom = H - padB;
    const scoreLabelY = (score, cy) =>
      score < 0 ? Math.min(cy + fScore + 8, plotBottom - 4) : cy - 10;

    const renderXLabel = (p, cx, has) => {
      const fill = has ? "#4b5563" : "#9ca3af";
      if (numeric) {
        return (
          <text x={cx} y={H - 28} textAnchor="middle" fontSize={fLabel} fontWeight="700" fill={p.color} fontFamily="inherit">
            {numLabel(p.label)}
          </text>
        );
      }
      const spaceIdx = p.label.indexOf(" ");
      if (twoLine && spaceIdx > -1) {
        return (
          <text x={cx} y={H - 26} textAnchor="middle" fontSize={fLabel} fill={fill} fontFamily="inherit">
            <tspan x={cx}>{p.label.slice(0, spaceIdx)}</tspan>
            <tspan x={cx} dy={fLabel + 3}>{p.label.slice(spaceIdx + 1)}</tspan>
          </text>
        );
      }
      return (
        <text x={cx} y={H - 10} textAnchor="middle" fontSize={fLabel} fill={fill} fontFamily="inherit">
          {p.label}
        </text>
      );
    };

    return (
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: "block" }}
        role="img"
        aria-label="Score progression across attempts"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={toY(t)} y2={toY(t)} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padL - 10} y={toY(t) + 3.5} textAnchor="end" fontSize={fAxis} fill="#9ca3af" fontFamily="inherit">
              {Math.round(t)}
            </text>
          </g>
        ))}

        {numeric && (
          <text x={padL + innerW / 2} y={H - 8} textAnchor="middle" fontSize={fAxis + 2} fontWeight="500" fill="#6b7280" fontFamily="inherit">
            Attempt
          </text>
        )}

        <text
          x={titleX}
          y={padT + innerH / 2}
          textAnchor="middle"
          fontSize={fAxis}
          fill="#9ca3af"
          fontFamily="inherit"
          transform={`rotate(-90 ${titleX} ${padT + innerH / 2})`}
        >
          Score
        </text>

        {areaD && <path d={areaD} fill="#3B82F6" opacity="0.06" />}

        {points.map((p, i) => {
          if (p.score === null || p.score === 0) return null;
          const yv = toY(p.score);
          return (
            <rect
              key={`bar-${i}`}
              x={toX(i) - barW / 2}
              y={Math.min(yv, y0)}
              width={barW}
              height={Math.abs(y0 - yv)}
              rx="4"
              fill={p.color}
              opacity="0.3"
            />
          );
        })}

        {linePts.length > 1 && (
          <path d={lineD} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        )}

        {points.map((p, i) => {
          const cx = toX(i);
          const has = p.score !== null;
          const cy = has ? toY(p.score) : y0;
          return (
            <g key={`pt-${i}`}>
              {has ? (
                <>
                  <text
                    x={cx}
                    y={scoreLabelY(p.score, cy)}
                    textAnchor="middle"
                    fontSize={fScore}
                    fontWeight="600"
                    fill={p.color}
                    fontFamily="inherit"
                  >
                    {p.score}
                  </text>
                  <circle cx={cx} cy={cy} r={dotR} fill={p.color} stroke="#fff" strokeWidth="1.5" />
                </>
              ) : (
                <circle cx={cx} cy={y0} r={dotR - 1} fill="#fff" stroke="#d1d5db" strokeWidth="1.5" />
              )}
              {renderXLabel(p, cx, has)}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5 md:p-6 min-w-0 flex flex-col flex-1 h-full">
      <div className="flex items-center gap-3">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#3B82F6" aria-hidden="true">
          <rect x="3" y="12" width="4" height="9" rx="1" />
          <rect x="10" y="7" width="4" height="14" rx="1" />
          <rect x="17" y="3" width="4" height="18" rx="1" />
        </svg>
        <h2 className="text-lg md:text-xl font-bold text-gray-900">Score Progression</h2>
      </div>
      <p className="text-sm text-gray-400 mt-1 ml-[30px]">
        Your score in each of the {points.length} attempts
      </p>

      <div className="mt-4 flex-1 flex items-center">
        <div className="w-full md:hidden">{renderChart(MOBILE)}</div>
        <div className="w-full hidden md:block">{renderChart(DESKTOP)}</div>
      </div>

      {scored.length === 0 && (
        <p className="text-xs text-gray-400 text-center mt-1">
          Complete an attempt to see your score progression here.
        </p>
      )}
    </div>
  );
}